import { isValidNumber } from '../../utils/numberUtils';
import { sortLocationsByMarketplacePriority } from '../../../../../../utils/location/sort-locations-by-marketplace-priority';

export interface SalaryStatisticsLike {
  median: { min: number; max: number };
  average: { min: number; max: number };
  min: number;
  max: number;
  percentile25: { min: number; max: number };
  percentile75: { min: number; max: number };
  percentile90?: { min: number; max: number };
  sampleSize: number;
  medianDaysOpen?: number;
  daysOpenSampleSize?: number;
}

export interface AllSalaryStatsLike {
  titleLocation?: Record<string, SalaryStatisticsLike>;
  companyTitleLocation?: Record<string, SalaryStatisticsLike>;
  title?: SalaryStatisticsLike;
  companyTitle?: SalaryStatisticsLike;
  locations?: Record<string, SalaryStatisticsLike>;
  roleLevel?: SalaryStatisticsLike;
  companyLocation?: Record<string, SalaryStatisticsLike>;
  titleRoleLevel?: SalaryStatisticsLike;
}

export type PrimaryBenchmarkKind =
  | 'titleLocation'
  | 'companyLocation'
  | 'locationMarket'
  | 'titleRoleLevel'
  | 'title'
  | 'locationSalaryStats'
  | 'companySalaryStats';

export interface PrimarySalaryBenchmark {
  stats: SalaryStatisticsLike;
  kind: PrimaryBenchmarkKind;
  /** The matched location string, only set for location-keyed benchmark kinds. */
  location?: string;
}

function getValidSampleSize(stats: SalaryStatisticsLike | undefined | null): number {
  if (!stats) return 0;
  return isValidNumber(stats.sampleSize) && stats.sampleSize >= 0 ? stats.sampleSize : 0;
}

function findStatsInRecord(
  record: Record<string, SalaryStatisticsLike> | undefined,
  locations: string[],
): { stats: SalaryStatisticsLike; location: string } | null {
  if (!record || Object.keys(record).length === 0) return null;
  for (const location of sortLocationsByMarketplacePriority(locations)) {
    const stats = record[location];
    if (stats && getValidSampleSize(stats) > 0) {
      return { stats, location };
    }
  }
  return null;
}

/**
 * Picks the single most-specific salary benchmark available, in the exact same priority order
 * `SalaryDistributionChart.tsx`'s `collectSalaryRanges` uses for its own `marketReferenceRange`
 * (titleLocation > companyLocation > locationMarket > titleRoleLevel > title), so the editorial-
 * rail redesign's condensed one-line summary agrees with what the full chart would say if
 * expanded. Deliberately duplicated rather than imported from that component: extracting it
 * would mean refactoring a large, heavily-used chart component for a single call site here,
 * more risk than the ~40 lines of duplication are worth.
 */
export function selectPrimarySalaryBenchmark(params: {
  allSalaryStats?: AllSalaryStatsLike;
  jobLocation?: string[];
  /** Per-location fallback (same shape as allSalaryStats.locations); used only when allSalaryStats has nothing usable. */
  locationSalaryStats?: Record<string, SalaryStatisticsLike>;
  /** Company-wide fallback stat; lowest priority, used only when nothing more specific is available. */
  companySalaryStats?: SalaryStatisticsLike;
}): PrimarySalaryBenchmark | null {
  const { allSalaryStats, jobLocation = [], locationSalaryStats, companySalaryStats } = params;

  if (allSalaryStats?.titleLocation && jobLocation.length > 0) {
    const found = findStatsInRecord(allSalaryStats.titleLocation, jobLocation);
    if (found) return { stats: found.stats, kind: 'titleLocation', location: found.location };
  }
  if (allSalaryStats?.companyLocation && jobLocation.length > 0) {
    const found = findStatsInRecord(allSalaryStats.companyLocation, jobLocation);
    if (found) return { stats: found.stats, kind: 'companyLocation', location: found.location };
  }
  if (allSalaryStats?.locations && jobLocation.length > 0) {
    const found = findStatsInRecord(allSalaryStats.locations, jobLocation);
    if (found) return { stats: found.stats, kind: 'locationMarket', location: found.location };
  }
  if (allSalaryStats?.titleRoleLevel && getValidSampleSize(allSalaryStats.titleRoleLevel) > 0) {
    return { stats: allSalaryStats.titleRoleLevel, kind: 'titleRoleLevel' };
  }
  if (allSalaryStats?.title && getValidSampleSize(allSalaryStats.title) > 0) {
    return { stats: allSalaryStats.title, kind: 'title' };
  }
  if (locationSalaryStats && jobLocation.length > 0) {
    const found = findStatsInRecord(locationSalaryStats, jobLocation);
    if (found) return { stats: found.stats, kind: 'locationSalaryStats', location: found.location };
  }
  if (companySalaryStats && getValidSampleSize(companySalaryStats) > 0) {
    return { stats: companySalaryStats, kind: 'companySalaryStats' };
  }
  return null;
}
